import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CloseCircle, Component, Element3, Gallery, Bag2 } from "iconsax-react";

// Icons swapped to Iconsax (Rahim, 2026-08-24: "use better icons"), reusing
// the same glyphs already established elsewhere for the same concepts —
// Element3 for a carousel of cards (TemplateSendPreview.jsx's own carousel
// icon), Gallery for image/video media (same placeholder icon used for
// IMAGE/VIDEO headers). No color tint on any of them (Rahim: "no colour
// needed") — plain text.secondary, same as every other row icon this pass.
const INSTAGRAM_TEMPLATE_TYPES = [
  { id: "button", label: "Button", description: "Text with up to 3 buttons (URL, postback, phone)", icon: Component },
  { id: "generic", label: "Generic", description: "Carousel of cards with image, title, subtitle, buttons", icon: Element3 },
  { id: "media", label: "Media", description: "Single image or video with optional buttons", icon: Gallery },
  { id: "product", label: "Product", description: "Product from catalog (catalog ID)", icon: Bag2 },
];

const FACEBOOK_TEMPLATE_TYPES = [
  { id: "button", label: "Button", description: "Text with up to 3 buttons (URL, postback, phone)", icon: Component },
  { id: "generic", label: "Generic", description: "Carousel of cards with image, title, subtitle, buttons", icon: Element3 },
  { id: "media", label: "Media", description: "Single image or video with optional buttons", icon: Gallery },
  { id: "product", label: "Product", description: "Product from catalog (catalog ID)", icon: Bag2 },
];

/** @deprecated use INSTAGRAM_TEMPLATE_TYPES or platform-specific list */
const TEMPLATE_TYPES = INSTAGRAM_TEMPLATE_TYPES;

/**
 * First dialog: list of template types. On select, parent opens the form for that type.
 * Platform-aware: Instagram and Facebook both use button, generic, media, product (List template deprecated by Meta).
 * Do NOT call onClose() when selecting a type - parent switches to form dialog via state.
 */
const InstagramTemplateSend = ({ open, onClose, onSelectType, platform = "instagram" }) => {
  const theme = useTheme();
  const templateTypes = platform === "facebook" ? FACEBOOK_TEMPLATE_TYPES : INSTAGRAM_TEMPLATE_TYPES;
  const title = platform === "facebook" ? "Send Messenger template" : "Send Instagram template";

  const handleSelect = (typeId) => {
    onSelectType(typeId);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: { xs: 2, sm: 3 },
          minHeight: { xs: "min(72vh, 480px)", sm: 520 },
          height: "auto",
          maxHeight: { xs: "92vh", sm: "85vh" },
          m: { xs: 1, sm: 2 },
          width: { xs: "calc(100% - 16px)", sm: undefined },
        },
      }}
    >
      <DialogContent sx={{ padding: 0, minHeight: { xs: "min(65vh, 420px)", sm: 480 }, overflowX: "hidden" }}>
        {/* Title — was h5, responsively bumping to 1.5rem, no bottom divider.
            Flattened to the Section Title token (1rem/600) and given the same
            header/divider treatment as every other dialog this session
            (Rahim, 2026-08-24: "use our new ui"). */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1, sm: 1.5 },
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "text.primary" }}>
              {title}
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: "0.8125rem", mt: 0.25 }}>
              Choose a template type, then fill in the fields and send.
            </Typography>
          </Box>
          <IconButton size="small" aria-label="Close dialog" onClick={onClose}>
            <CloseCircle size="20" color={theme.palette.text.primary} variant="Linear" />
          </IconButton>
        </Box>
        {/* Rows switched to the same rounded-pill/inset treatment used across
            every list this session (AllTemplates.jsx, InboxSettings.jsx) —
            was flat full-bleed rows with a divider between them. */}
        <List sx={{ p: 0 }}>
          {templateTypes.map((t, index) => {
            const Icon = t.icon;
            return (
              <ListItem
                key={t.id}
                onClick={() => handleSelect(t.id)}
                sx={{
                  cursor: "pointer",
                  borderRadius: "8px",
                  mx: 1,
                  mt: index === 0 ? 1 : 0.5,
                  mb: index === templateTypes.length - 1 ? 1 : 0,
                  py: { xs: 1, sm: 1.25 },
                  px: { xs: 1.25, sm: 1.5 },
                  transition: "background-color 0.12s ease",
                  "&:hover": {
                    backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: { xs: 40, sm: 44 } }}>
                  <Icon size={26} color={theme.palette.text.secondary} variant="Linear" />
                </ListItemIcon>
                <ListItemText
                  primary={t.label}
                  secondary={t.description}
                  primaryTypographyProps={{ sx: { fontWeight: 500, fontSize: "0.9375rem", color: "text.primary" } }}
                  secondaryTypographyProps={{ sx: { fontSize: "0.8125rem", color: "text.secondary", mt: 0.25 } }}
                />
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default InstagramTemplateSend;
export { TEMPLATE_TYPES, INSTAGRAM_TEMPLATE_TYPES, FACEBOOK_TEMPLATE_TYPES };
